import { firestore } from "../firebase";
import DB_PATH from "../DB_PATH";

// Client reads for the ad engine state the MCP ad spy writes under
// Community/{id}: AdEngine/config (watchlist), AdSwipes (competitor ads with
// run-length tracking), AdReports (dated scans). The dashboard reads these
// and only ever writes swipe status/notes; scanning stays MCP-side.

export type AdWatchEntry = {
  platform?: string;
  name?: string;
  pageId?: string;
  notes?: string;
};

export type AdEngineClientConfig = {
  watchlist: AdWatchEntry[];
  keywords: string[];
  countries: string[];
  lastScanAt: string | null;
  latestAdReportId: string | null;
};

export type AdSwipeStatus = "new" | "shortlisted" | "recreated" | "dismissed";

export type AdSwipeDoc = {
  id: string;
  platform: string;
  adArchiveId: string;
  advertiserName: string;
  advertiserId?: string;
  bodyText?: string;
  title?: string;
  cta?: string;
  creativeType?: string;
  snapshotUrl?: string;
  creativeUrls: string[];
  landingUrl?: string;
  countries: string[];
  status: AdSwipeStatus;
  active: boolean;
  firstSeenAt: string | null;
  lastSeenAt: string | null;
  notes?: string;
  recreatedRefId?: string;
  /** Creatives mirrored into our Storage by the MCP (permanent URLs). */
  storedCreatives: { url: string; contentType: string }[];
};

export type AdReportDoc = {
  id: string;
  date?: string;
  summary?: string;
  sources?: string[];
  advertisers?: { name?: string; platform?: string; activeAds?: number; newAds?: number; goneAds?: number }[];
  createdAt?: string;
};

const communityDoc = (communityId: string) =>
  firestore.collection(DB_PATH.COMMUNITY).doc(communityId);

const str = (v: any): string | undefined =>
  typeof v === "string" && v ? v : undefined;

export const getAdEngineClientConfig = async (
  communityId: string
): Promise<AdEngineClientConfig | null> => {
  const snap = await communityDoc(communityId)
    .collection("AdEngine")
    .doc("config")
    .get();
  if (!snap.exists) return null;
  const d: any = snap.data() || {};
  return {
    watchlist: Array.isArray(d.watchlist)
      ? d.watchlist.filter((w: any) => w && typeof w === "object")
      : [],
    keywords: Array.isArray(d.keywords)
      ? d.keywords.filter((k: any) => typeof k === "string")
      : [],
    countries: Array.isArray(d.countries)
      ? d.countries.filter((c: any) => typeof c === "string")
      : [],
    lastScanAt: str(d.lastScanAt) || null,
    latestAdReportId: str(d.latestAdReportId) || null,
  };
};

const sanitizeSwipe = (id: string, d: any): AdSwipeDoc => ({
  id,
  platform: str(d?.platform) || "meta",
  adArchiveId: str(d?.adArchiveId) || "",
  advertiserName: str(d?.advertiserName) || "Unknown advertiser",
  advertiserId: str(d?.advertiserId),
  bodyText: str(d?.bodyText),
  title: str(d?.title),
  cta: str(d?.cta),
  creativeType: str(d?.creativeType),
  snapshotUrl: str(d?.snapshotUrl),
  creativeUrls: Array.isArray(d?.creativeUrls)
    ? d.creativeUrls.filter((u: any) => typeof u === "string")
    : [],
  landingUrl: str(d?.landingUrl),
  countries: Array.isArray(d?.countries)
    ? d.countries.filter((c: any) => typeof c === "string")
    : [],
  status: (str(d?.status) as AdSwipeStatus) || "new",
  active: d?.active !== false,
  firstSeenAt: str(d?.firstSeenAt) || null,
  lastSeenAt: str(d?.lastSeenAt) || null,
  notes: str(d?.notes),
  recreatedRefId: str(d?.recreatedRefId),
  storedCreatives: Array.isArray(d?.storedCreatives)
    ? d.storedCreatives
        .filter((c: any) => c && typeof c.url === "string")
        .map((c: any) => ({
          url: c.url,
          contentType: typeof c.contentType === "string" ? c.contentType : "image/jpeg",
        }))
    : [],
});

export const listAdSwipesClient = async (
  communityId: string,
  limit = 300
): Promise<AdSwipeDoc[]> => {
  const col = communityDoc(communityId).collection("AdSwipes");
  try {
    const snap = await col.orderBy("lastSeenAt", "desc").limit(limit).get();
    return snap.docs.map((d) => sanitizeSwipe(d.id, d.data()));
  } catch {
    const snap = await col.limit(limit).get();
    return snap.docs
      .map((d) => sanitizeSwipe(d.id, d.data()))
      .sort((a, b) => (b.lastSeenAt || "").localeCompare(a.lastSeenAt || ""));
  }
};

export const getLatestAdReport = async (
  communityId: string
): Promise<AdReportDoc | null> => {
  const snap = await communityDoc(communityId).collection("AdReports").get();
  if (snap.empty) return null;
  const latest = snap.docs
    .map((d) => ({ id: d.id, ...(d.data() as any) }))
    .sort((a, b) => b.id.localeCompare(a.id))[0];
  return latest as AdReportDoc;
};

// The one client-side write: the coach triaging a swipe from the dashboard.
export const updateAdSwipeStatus = async (
  communityId: string,
  id: string,
  patch: { status?: AdSwipeStatus; notes?: string }
): Promise<void> => {
  await communityDoc(communityId)
    .collection("AdSwipes")
    .doc(id)
    .set({ ...patch, updatedAt: new Date().toISOString() }, { merge: true });
};

// Run length in whole days between first and last confirmed sighting.
export const swipeRunDays = (s: AdSwipeDoc): number => {
  if (!s.firstSeenAt || !s.lastSeenAt) return 0;
  const ms = Date.parse(s.lastSeenAt) - Date.parse(s.firstSeenAt);
  return Number.isFinite(ms) ? Math.max(0, Math.round(ms / 86400_000)) : 0;
};
